// Kept free of imports and side effects so tests can import these without pulling in the rest of
// checkout-utils, which reaches for lander runtime and path-aliased element types.

export interface ServerCheckoutErrorDetail {
  code?: string
  message?: string
  graceful_message?: string
  source?: string
}

interface ServerCheckoutErrorBody {
  error?: string
  message?: string
  // Polymorphic on the wire: Rebilly's handler pushes an array of details, while
  // Stripe::Processor rescues a CardError into a bare `{ code }` hash.
  error_details?: ServerCheckoutErrorDetail[] | ServerCheckoutErrorDetail
}

export interface NormalizedServerCheckoutError {
  code?: string
  message?: string
  // Present only when the input was our envelope. A normalised network error or Stripe error must
  // still read as "not ours", or the modal fallback below would be unreachable on Stripe, where
  // handleError only ever sees normalised objects.
  error_details?: ServerCheckoutErrorDetail[]
}

// The one test for "did this come from our checkout endpoint". Both helpers below key off it,
// so a shape can never be trusted for its text but not its classification, or the reverse.
function isServerCheckoutErrorEnvelope(payload: ServerCheckoutErrorBody): boolean {
  return typeof payload?.error === 'string' || payload?.error_details != null
}

// Flattens the two wire shapes into one before anything downstream reads them. `code` has to
// survive: it is what makes a decline look like a Stripe error, which is the only thing that sets
// `paymentDeclined` and therefore the only thing that mounts a card field in OTO mode.
export function normalizeServerCheckoutError(body: unknown): NormalizedServerCheckoutError {
  const payload = (body ?? {}) as ServerCheckoutErrorBody
  const raw = payload.error_details
  // The bare-hash shape only ever comes from Stripe::Processor rescuing a CardError, so a detail
  // lifted out of it is gateway-sourced by construction. Stamping it lets isActionableCheckoutError
  // reach the same answer as isStripeError on its own, instead of agreeing by branch order.
  const details = Array.isArray(raw) ? raw : raw ? [{ source: 'payment_gateway', ...raw }] : []
  return {
    code: details[0]?.code,
    // `?? message` makes this a fixpoint: normalising an already-normalised object must not lose
    // the text, or a second defensive pass would strip the decline that mounts the card field.
    message: payload.error ?? payload.message,
    error_details: isServerCheckoutErrorEnvelope(payload) ? details : undefined,
  }
}

// Buyer-facing copy, so it only ever returns a string the server authored. A bare `{ message }`
// is refused: that shape also carries the `failed_payment_message` URL parameter and raw fetcher
// error types, neither of which should ever be shown to a buyer.
export function serverCheckoutErrorMessage(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null
  const payload = body as ServerCheckoutErrorBody
  if (!isServerCheckoutErrorEnvelope(payload)) return null

  const details = normalizeServerCheckoutError(payload).error_details ?? []
  if (details.length) {
    const joined = details
      .map((detail) => detail?.graceful_message ?? detail?.message)
      .filter(Boolean)
      .join(' ')
    if (joined) return joined
  }
  return payload.error || payload.message || null
}

// A decline is something the buyer can act on; "we are out of stock" is not. Only a response we
// positively recognise as our own rejection envelope is safe to call non-actionable -- anything
// else reaching here came from the payment layer, where handing back the form to retry is still
// the better answer.
export function isActionableCheckoutError(body: unknown): boolean {
  const payload = body as ServerCheckoutErrorBody
  const details = normalizeServerCheckoutError(payload).error_details ?? []
  if (details.some((detail) => detail?.source === 'payment_gateway')) {
    return true
  }
  return !isServerCheckoutErrorEnvelope(payload)
}
